import React, { useState, useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import Dropdown from '../components/Dropdown';
import { useDashboardStats, useMissionChartData, useUserChartData, useRevenueChartData, useDashboardAlerts } from '../hooks/useDashboard';
import '../styles/Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const missionOptions = {
  responsive: true,
  plugins: { legend: { display: false } },
  scales: {
    y: {
      beginAtZero: false,
      min: 25,
      max: 28.5,
      ticks: { stepSize: 1, color: '#2D3748' },
      grid: { color: '#E2E8F0' },
    },
    x: {
      ticks: { color: '#2D3748' },
      grid: { display: false },
    },
  },
};

const userOptions = {
  responsive: true,
  plugins: {
    legend: { display: false },
    title: { display: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { color: '#2D3748' },
      grid: { color: '#E2E8F0' },
    },
    x: {
      ticks: { color: '#2D3748' },
      grid: { display: false },
    },
  },
};

// === Composant Circulaire ===
const CircularStat = ({ percent, count, label, color }) => {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  
  return (
    <div className="stat-card">
      <div className="circular-progress">
        <svg width="80" height="80">
          <circle cx="40" cy="40" r={radius} stroke="#E5E7EB" strokeWidth="6" fill="none" />
          <circle
            cx="40"
            cy="40"
            r={radius}
            stroke={color}
            strokeWidth="6"
            fill="none"
            strokeDasharray={`${(percent / 100) * circumference}, ${circumference}`}
            transform="rotate(-90 40 40)"
          />
        </svg>
        <div className="percent">{percent}%</div>
      </div>
      <div className="stat-info">
        <strong>{count}</strong>
        <div>{label}</div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [selectedMonth, setSelectedMonth] = useState('mois');
  const [selectedChartType, setSelectedChartType] = useState('courbe');

  // Hooks pour les données dynamiques
  const { stats, loading: statsLoading } = useDashboardStats();
  const { chartData: missionChartData, loading: missionLoading } = useMissionChartData();
  const { chartData: userChartData, loading: userLoading } = useUserChartData();
  const { chartData: revenueChartData, loading: revenueLoading } = useRevenueChartData();
  const { alerts, loading: alertsLoading } = useDashboardAlerts();

  const monthOptions = [
    { value: 'mois', label: 'Mois' },
    { value: 'semaine', label: 'Semaine' },
    { value: 'annee', label: 'Année' }
  ];

  const chartTypeOptions = [
    { value: 'courbe', label: 'Courbe' },
    { value: 'histogramme', label: 'Histogramme' },
    { value: 'barres', label: 'Barres' }
  ];

  // Données dynamiques pour le graphique des missions
  const missionData = useMemo(() => ({
    labels: missionChartData.labels.length > 0 ? missionChartData.labels : ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Mission en cours',
        data: missionChartData.inProgress.length > 0 ? missionChartData.inProgress : [0, 0, 0, 0, 0, 0],
        borderColor: '#E53E3E',
        backgroundColor: 'transparent',
        tension: 0.4,
      },
      {
        label: 'Mission terminée',
        data: missionChartData.completed.length > 0 ? missionChartData.completed : [0, 0, 0, 0, 0, 0],
        borderColor: '#3182CE',
        backgroundColor: 'transparent',
        tension: 0.4,
      },
    ],
  }), [missionChartData]);

  // Données dynamiques pour le graphique des utilisateurs
  const userData = useMemo(() => ({
    labels: userChartData.labels.length > 0 ? userChartData.labels : ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Actif',
        data: userChartData.active.length > 0 ? userChartData.active : [0, 0, 0, 0, 0, 0],
        backgroundColor: '#3182CE',
      },
      {
        label: 'Non actif',
        data: userChartData.inactive.length > 0 ? userChartData.inactive : [0, 0, 0, 0, 0, 0],
        backgroundColor: '#E53E3E',
      },
    ],
  }), [userChartData]);

  // Données dynamiques pour le graphique des revenus
  const revenueData = useMemo(() => ({
    labels: revenueChartData.labels.length > 0 ? revenueChartData.labels : ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Payé',
        data: revenueChartData.paid.length > 0 ? revenueChartData.paid : [0, 0, 0, 0, 0, 0],
        borderColor: '#10B981',
        backgroundColor: 'transparent',
        tension: 0.4,
      },
      {
        label: 'Impayé',
        data: revenueChartData.unpaid.length > 0 ? revenueChartData.unpaid : [0, 0, 0, 0, 0, 0],
        borderColor: '#F59E0B',
        backgroundColor: 'transparent',
        tension: 0.4,
      },
    ],
  }), [revenueChartData]);

  return (
    <div className="dashboard-container">
      {/* === Section Graphiques === */}
      <div className="graph-section">
        <div className="block mission-block">
          <div className="block-header">
            <Dropdown
              options={monthOptions}
              value={selectedMonth}
              onChange={(option) => setSelectedMonth(option.value)}
              size="sm"
              className="w-20"
            />
            <Dropdown
              options={chartTypeOptions}
              value={selectedChartType}
              onChange={(option) => setSelectedChartType(option.value)}
              size="sm"
              className="w-24"
            />
          </div>
          <h2 className="block-title">Missions</h2>
          <div className="legend">
            <span><span className="color-box red"></span>Mission en cours</span>
            <span><span className="color-box blue"></span>Mission terminée</span>
          </div>
          {missionLoading ? (
            <div className="chart-loading">Chargement...</div>
          ) : (
            <Line data={missionData} options={missionOptions} />
          )}
        </div>

        <div className="block utilisateurs-block">
          <div className="block-header">
            <Dropdown
              options={monthOptions}
              value={selectedMonth}
              onChange={(option) => setSelectedMonth(option.value)}
              size="sm"
              className="w-20"
            />
            <Dropdown
              options={chartTypeOptions}
              value={selectedChartType}
              onChange={(option) => setSelectedChartType(option.value)}
              size="sm"
              className="w-24"
            />
          </div>
          <h2 className="block-title">Utilisateurs</h2>
          {userLoading ? (
            <div className="chart-loading">Chargement...</div>
          ) : (
            <Bar data={userData} options={userOptions} />
          )}
          <div className="legend">
            <span><span className="color-box blue"></span>Actif</span>
            <span><span className="color-box red"></span>Non actif</span>
          </div>
        </div>

        <div className="block revenus-block">
          <h3 className="title-centered">Revenus</h3>
          <div className="legend-container">
            <div className="legend-item"><span className="legend-color green"></span>Payé</div>
            <div className="legend-item"><span className="legend-color orange"></span>Impayé</div>
          </div>
          {revenueLoading ? (
            <div className="chart-loading">Chargement...</div>
          ) : (
            <Line data={revenueData} options={{ plugins: { legend: { display: false } } }} />
          )}
        </div>
      </div>

      {/* === Section Statistiques circulaires === */}
      <div className="stats-section">
        <div className="stat-header">
          <Dropdown
            options={monthOptions}
            value={selectedMonth}
            onChange={(option) => setSelectedMonth(option.value)}
            size="sm"
            className="w-20"
          />
        </div>
        {statsLoading ? (
          <div className="stats-loading">Chargement des statistiques...</div>
        ) : (
          <div className="stat-grid">
            <CircularStat
              percent={stats.users.percent}
              count={stats.users.total}
              label="Utilisateurs inscrits"
              color="#3B82F6"
            />
            <CircularStat
              percent={stats.prestataires.percent}
              count={stats.prestataires.active}
              label="Prestataires actifs"
              color="#3B82F6"
            />
            <CircularStat
              percent={stats.missions.percent}
              count={stats.missions.inProgress}
              label="Missions en cours"
              color="#3B82F6"
            />
            <CircularStat
              percent={stats.commissions.percent}
              count={stats.commissions.total.toLocaleString('fr-FR')}
              label="Commissions collectées (CFA)"
              color="#3B82F6"
            />
          </div>
        )}
      </div>

      {/* === Alertes & Documents === */}
      <div className="right-side">
        <div className="card alerts-card">
          <h3 className="card-title">Alertes à traiter</h3>
          {alertsLoading ? (
            <div className="alerts-loading">Chargement...</div>
          ) : (
            <>
              <div className="card-line">
                <span>Signalements</span>
                <span className="badge">{String(alerts.signalements).padStart(2, '0')}</span>
              </div>
              <div className="card-line">
                <span>Paiement en retard</span>
                <span className="badge">{String(alerts.paiementsRetard).padStart(2, '0')}</span>
              </div>
            </>
          )}
        </div>
        <div className="card documents-card">
          <h3 className="card-title">Documents en attente</h3>
          {alertsLoading ? (
            <div className="alerts-loading">Chargement...</div>
          ) : (
            <>
              <div className="card-line">
                <span>A valider</span>
                <span className="badge">{String(alerts.documentsAValider).padStart(2, '0')}</span>
              </div>
              <div className="card-line">
                <span>Refusés</span>
                <span className="badge">{String(alerts.documentsRefuses).padStart(2, '0')}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
